/**
 * video.js — lazy video playback with optional HLS (port of hooks/use-hls.ts
 * + UGC video carousel playback behavior).
 *
 * Markup contract:
 *   <video data-lazy-video data-src="https://….m3u8|.mp4|.webm"
 *          muted playsinline loop preload="none" poster="…"></video>
 *
 * - .m3u8 sources use hls.js (vendor-hls.js) where MSE is needed; Safari
 *   plays HLS natively.
 * - Videos start when scrolled into view and pause when leaving it.
 */
(function () {
  "use strict";

  function attachSource(video) {
    if (video.__sourced) return;
    video.__sourced = true;
    const src = video.getAttribute("data-src");
    if (!src) return;

    if (/\.m3u8(\?|$)/.test(src)) {
      if (video.canPlayType("application/vnd.apple.mpegurl")) {
        video.src = src;
      } else if (window.Hls && window.Hls.isSupported()) {
        const hls = new window.Hls({ maxBufferLength: 10 });
        hls.loadSource(src);
        hls.attachMedia(video);
        video.__hls = hls;
      } else {
        // Last resort: try a sibling fallback source if provided.
        const fallback = video.getAttribute("data-fallback-src");
        if (fallback) video.src = fallback;
      }
    } else {
      video.src = src;
    }
  }

  const io =
    "IntersectionObserver" in window
      ? new IntersectionObserver(
          (entries) => {
            for (const entry of entries) {
              const video = entry.target;
              if (entry.isIntersecting) {
                attachSource(video);
                const p = video.play();
                if (p && p.catch) p.catch(() => {});
              } else if (!video.paused) {
                video.pause();
              }
            }
          },
          { threshold: 0.25 },
        )
      : null;

  function scan(root) {
    (root || document).querySelectorAll("[data-lazy-video]").forEach((video) => {
      if (video.__observed) return;
      video.__observed = true;
      if (io) {
        io.observe(video);
      } else {
        attachSource(video);
        const p = video.play();
        if (p && p.catch) p.catch(() => {});
      }
    });
  }

  /* ------------------------------------------------------------------ */
  /* Click-to-play videos (port of UGCVideoCarousel.tsx).                */
  /* Unlike the lazy autoplay above, these load + play WITH SOUND on a    */
  /* click, one at a time; clicking a playing video pauses it. The        */
  /* [data-ugc-play] overlay reflects state, and a [data-ugc-loading]     */
  /* spinner shows while the source buffers. Markup contract:             */
  /*   <div data-ugc-slide>                                               */
  /*     <video data-ugc-video data-src="…" playsinline loop preload=none>*/
  /*     <div data-ugc-loading hidden>…</div>                             */
  /*     <button data-ugc-play>…</button>                                 */
  /*   </div>                                                             */
  /* ------------------------------------------------------------------ */
  function pauseClickVideos(except, scope) {
    (scope || document).querySelectorAll("[data-ugc-video]").forEach((v) => {
      if (v !== except && !v.paused) v.pause();
    });
  }

  function initClickToPlay(root) {
    (root || document).querySelectorAll("[data-ugc-video]").forEach((video) => {
      if (video.__ctp) return;
      video.__ctp = true;
      const slide = video.closest("[data-ugc-slide]") || video.parentElement;
      const playBtn = slide && slide.querySelector("[data-ugc-play]");
      const spinner = slide && slide.querySelector("[data-ugc-loading]");

      function toggle(el, on) {
        if (el) el.toggleAttribute("hidden", !on);
      }
      function setState(state) {
        // idle/paused -> play button; loading -> spinner; playing -> video only
        toggle(playBtn, state === "idle" || state === "paused");
        toggle(spinner, state === "loading");
      }

      function start() {
        setState("loading");
        attachSource(video);
        video.muted = false;
        pauseClickVideos(video);
        const p = video.play();
        if (p && p.catch) p.catch(() => setState("idle"));
      }

      video.addEventListener("playing", () => setState("playing"));
      video.addEventListener("pause", () => setState("paused"));
      if (playBtn) playBtn.addEventListener("click", start);
      video.addEventListener("click", () => {
        if (video.paused) start();
        else video.pause();
      });

      setState("idle");
    });
  }

  // Pause a playing click-to-play video when its carousel changes slides.
  document.addEventListener("carousel:select", (e) => {
    pauseClickVideos(
      null,
      e.target && e.target.querySelectorAll ? e.target : document,
    );
  });

  // Pause playback when the tab is hidden (port of the visibilitychange handler).
  document.addEventListener("visibilitychange", () => {
    if (document.hidden) pauseClickVideos(null);
  });

  window.themeUtils.onReady(() => {
    scan();
    initClickToPlay();
  });
  document.addEventListener("video:scan", (e) => {
    scan(e.target);
    initClickToPlay(e.target);
  });
  window.themeVideos = { scan: scan, initClickToPlay: initClickToPlay };
})();
